import { KeplerColorMap, KeplerColorRange, KeplerScaleType } from 'features/builder/types'
import { _getLog10ScaleSteps as getLog10ScaleSteps } from '@carto/api-client'
import { getTopValuesSorted } from './raster/rasterLayerConfig'
export type FieldStats = {
  min: number
  max: number

  // quantiles, not including min and max
  quantiles?: Record<number, number[]>

  // in case or rasters colored as ordinal
  top_values?: Record<number, number>
}

export const UNKNOWN_COLOR = [134, 141, 145]

export function getLog10ScaleColorMap({
  min,
  max,
  colors
}: {
  min: number
  max: number
  colors: string[]
}): KeplerColorMap {
  return [
    ...getLog10ScaleSteps({ min, max, steps: colors.length }).map((value, index): [number | null, string] => [
      value,
      colors[index]
    ]),
    [null, colors[colors.length - 1]]
  ]
}

export function colorMapsEqual(a: KeplerColorMap, b: KeplerColorMap) {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    const [aKey, aColor] = a[i]
    const [bKey, bColor] = b[i]
    if (aKey !== bKey || aColor !== bColor) return false
  }
  return true
}

export function createColorDomain({
  scaleType,
  colorRange,
  stats
}: {
  scaleType: KeplerScaleType
  colorRange: KeplerColorRange
  stats?: FieldStats
}): Array<number | string | null> {
  if (scaleType === KeplerScaleType.Ordinal) {
    if (stats?.top_values) {
      return getTopValuesSorted(stats?.top_values).slice(0, colorRange.colors.length)
    }
    return colorRange.colorMap?.map(([value]) => value) || []
  }

  if (!stats) {
    return [0, 1]
  }
  if (scaleType === KeplerScaleType.Custom) {
    if (colorRange.uiCustomScaleType === 'logarithmic') {
      return [stats.min, stats.max]
    } else {
      // actually custom, read colorMap
      return colorRange.colorMap?.map(([value]) => value) || []
    }
  } else if (scaleType === KeplerScaleType.Quantile) {
    const steps = colorRange.colors.length
    const quantiles = stats.quantiles?.[steps]
    if (!quantiles) {
      return [0, 1]
    }
    // we expect quantiles domain in form [min, ...quantiles, max]
    if (quantiles.length < steps) {
      // some APIs (raster metadata) return quantiles as only _internal_ steps and we expect min and max here
      return [stats.min, ...quantiles, stats.max]
    } else {
      // some APIs (stats api) return quantiles _with_ min and max included
      return quantiles
    }
  } else {
    return [stats.min, stats.max]
  }
}
